import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { EMPTY_FILTER_ID } from "constants/app.constants";
import { RootState } from "store/rootReducer";
import { selectHasXdaLine, useMapStore } from "stores/map";

import { getUwisInFilter } from "api/filter";
import { fetchPerpendicularLine, postWellsIntersect } from "api/map";

import { useDashboardContext } from "components/dashboard/hooks/useDashboardContext";

import { legUwisForWells } from "../utils/legUwisForWells";

/**
 * Narrow the crossed wells to the active filter, the same `/contains` re-check
 * the lasso path runs (`mapboxHelper.ts:290-325`). Without it the cross-section
 * shows wells the filter has removed from every other view on the dashboard.
 * With no filter there is nothing to scope to, so the list passes through — the
 * lasso path returns nothing instead, but only because it also has a viewport
 * that is rendering nothing.
 */
async function scopeToFilter(uwis: string[], filterId: Maybe<string>): Promise<string[]> {
  // EMPTY_FILTER_ID is a truthy sentinel for "no filter applied". Never POST it
  // to /contains as if it were a real filter id.
  if (!filterId || filterId === EMPTY_FILTER_ID || uwis.length === 0) return uwis;

  const { status, data } = await getUwisInFilter(filterId, uwis);
  if (status !== 200 || !Array.isArray(data)) {
    throw new Error("Unexpected response from /contains");
  }
  const inFilter = new Set(data);
  return uwis.filter((uwi) => inFilter.has(uwi));
}

/**
 * Drop the shared section line. Both store entries go, or the line stays
 * painted on the map: `XdaViewer` answers a line request by writing
 * `xdaInterceptData`, and `XdaLineMarker` paints whatever is in it and never
 * erases it. On a dashboard with neither an XDA nor a 3D widget nothing else
 * would take it down, so the section would sit across the map with nothing
 * left to explain it.
 */
const clearSharedLine = (): void => {
  const map = useMapStore.getState();
  map.setXdaLineRequest({ line: [], wells: [] });
  map.setXdaInterceptData({ line: [], points: [] });
};

export interface DeriveXdaLineResult {
  readonly isDeriving: boolean;
  /**
   * Is there a section line to draw? Read from the map store, not from this
   * hook's own bookkeeping: a user-drawn line is the same store entry, and the
   * gunbarrel draws whichever line is there.
   */
  readonly hasLine: boolean;
  /** The perpendicular-line request failed. Distinct from "no survey". */
  readonly isError: boolean;
  /**
   * The line stands, but the wells it crosses could not be fetched, so the
   * section holds the target well alone.
   */
  readonly neighboursUnavailable: boolean;
  readonly retry: () => void;
}

/**
 * The panel derives its own cross-section line over HTTP. `XdaLineMarker`'s
 * derive cannot serve it: that path needs a widget on the dashboard, a well
 * layer, and a source feature the map is actually rendering, none of which the
 * panel can rely on.
 */
export function useDeriveXdaLineForWell(
  uwi: Maybe<string>,
  halfLengthMeters: number,
  /** A user-drawn line is on screen: derive nothing and touch nothing. */
  customLineActive = false
): DeriveXdaLineResult {
  const setXdaLineRequest = useMapStore((s) => s.setXdaLineRequest);
  const hasLine = useMapStore(selectHasXdaLine);
  const filterId = useSelector((state: RootState) => state.filter.filterId?.id);
  const [isDeriving, setIsDeriving] = useState(false);
  const [isError, setIsError] = useState(false);
  const [neighboursUnavailable, setNeighboursUnavailable] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // True only once this hook has written the shared line. Without it, losing
  // the target well would clear a line the card never wrote — one the user
  // drew, or one a dashboard widget derived before the card was pointed at
  // anything.
  const ownsLineRef = useRef(false);

  // Read on the way out, when the closure below has long since been made, so
  // it is held in a ref rather than captured.
  //
  // Both viewers read `xdaLineRequest`, so either one on the dashboard is a
  // widget drawing this line. The same union `XdaLineMarker` uses to decide
  // it is on a dashboard that shows sections at all.
  const { capabilities } = useDashboardContext();
  const dashboardDrawsSectionRef = useRef(false);
  dashboardDrawsSectionRef.current = !!(
    capabilities?.hasXdaWidget || capabilities?.hasThreeDWidget
  );

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;

    // The draw writes the line first and flags it custom second, so by the time
    // this runs the drawn line is already in the store. Hand ownership back
    // WITHOUT clearing — clearing here wipes the line the user just drew, and
    // the tab drops back to an empty section. Reset to auto flips this off and
    // the derive below runs again.
    if (customLineActive) {
      ownsLineRef.current = false;
      setIsDeriving(false);
      setIsError(false);
      setNeighboursUnavailable(false);
      return;
    }

    if (!uwi) {
      if (ownsLineRef.current) {
        ownsLineRef.current = false;
        clearSharedLine();
      }
      setIsDeriving(false);
      setIsError(false);
      setNeighboursUnavailable(false);
      return;
    }

    // A line left over from the previous target must never survive into the
    // next one — it would draw a cross-section for the wrong well.
    ownsLineRef.current = true;
    setXdaLineRequest({ line: [], wells: [] });
    setIsError(false);
    setNeighboursUnavailable(false);
    setIsDeriving(true);
    (async () => {
      let perpendicular;
      try {
        perpendicular = await fetchPerpendicularLine(uwi, halfLengthMeters);
      } catch (err) {
        const status = err?.response?.status;

        // map-svc answering 404, 400 or 422 is a settled answer: this well has
        // no survey to build a section line from. A vertical well is a normal
        // well, so the tab shows an empty section the user can draw into and
        // offers nothing to retry — the same ending as a request that resolves
        // with no line. Only a breadcrumb is left behind, to sit under whatever
        // error comes next.
        if (status === 404 || status === 400 || status === 422) {
          Sentry.addBreadcrumb({
            message: "No section line available for this well",
            category: "well_card_section_line",
            level: "info",
            data: { action: "fetchPerpendicularLine", uwi, halfLengthMeters, status }
          });
          if (cancelled) return;
          setIsDeriving(false);
          return;
        }

        // Everything else could work on a second try — a 5xx, a refused
        // session, and a request that never got an answer at all (no
        // `response`: network, timeout, a cancelled connection). The tab shows
        // a retry state, which is all the user needs. Nothing else records
        // that map-svc refused the request, so a well the derive never works
        // for is invisible from here.
        Sentry.withScope((scope) => {
          scope.setContext("well_card_section_line", {
            action: "fetchPerpendicularLine",
            uwi,
            halfLengthMeters
          });
          scope.setLevel("error");
          Sentry.captureException(err);
        });
        if (cancelled) return;
        setIsDeriving(false);
        setIsError(true);
        return;
      }
      if (cancelled) return;

      if (
        perpendicular?.[0]?.x == null ||
        perpendicular?.[1]?.x == null ||
        perpendicular?.[0]?.y == null ||
        perpendicular?.[1]?.y == null
      ) {
        setIsDeriving(false);
        return;
      }

      const line = [
        [perpendicular[0].x, perpendicular[0].y],
        [perpendicular[1].x, perpendicular[1].y]
      ];

      let neighbours: string[] = [];
      let neighboursFailed = false;
      try {
        neighbours = await scopeToFilter(
          Object.keys(await postWellsIntersect(line)),
          filterId
        );
      } catch (err) {
        // The section still draws, with the target well alone in it, so this
        // failure never reaches an error state. Only the notice above the
        // viewer says anything, and nothing at all records why.
        Sentry.withScope((scope) => {
          scope.setContext("well_card_section_line", {
            action: "postWellsIntersect",
            uwi,
            filterId
          });
          scope.setLevel("error");
          Sentry.captureException(err);
        });
        neighboursFailed = true;
      }
      if (cancelled) return;

      // The clicked well is unioned in: a one-well list buys a wide, nearly
      // empty section. It is not filter-scoped away either — the card is
      // showing it, so the section has to.
      const wells = Array.from(new Set([...neighbours, uwi]));

      // A multilateral's legs are never in this list: neither `/wells-intersect`
      // nor the filter's `/contains` answers for a leg id, so the legs of every
      // well the section crosses are dropped by the two steps above. Left out of
      // the request the XDA service does not draw them as wells at all — they
      // come back as polygons outside the well list, which the viewer paints as
      // grey crosshatch and can never colour. So they are re-derived from the
      // map layer that does carry them and appended, which is what the map's own
      // selection hands a dashboard section.
      const legs = legUwisForWells(wells).filter((leg) => !wells.includes(leg));
      setXdaLineRequest({ line, wells: legs.length ? [...wells, ...legs] : wells });
      setNeighboursUnavailable(neighboursFailed);
      setIsDeriving(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [uwi, halfLengthMeters, customLineActive, attempt, filterId, setXdaLineRequest]);

  useEffect(() => {
    return () => {
      /*
       * The card is closing. If the dashboard has a widget that draws this
       * line - the XDA viewer or the 3D one, both of which read
       * `xdaLineRequest` - taking it down would leave that widget with an
       * empty section until something moved the map selection again. So the
       * line is handed back rather than cleared: the widget keeps the section
       * it was already showing, and `XdaLineMarker`'s own derive - which
       * stands down only while the panel is open - takes over from the next
       * selection.
       *
       * With neither widget the line goes, whoever wrote it. Ownership does
       * not come into it: a line the user drew from the card's own draw button
       * is handed back to nothing on such a dashboard, and would sit across
       * the map with the card that explained it already gone.
       */
      if (dashboardDrawsSectionRef.current) return;
      clearSharedLine();
    };
  }, []);

  return { isDeriving, hasLine, isError, neighboursUnavailable, retry };
}
